import React, { useState, useEffect, useMemo } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { Alert, Button, Card, Table, Pagination } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import Loading from '~/Components/Loading';
import { salvarEmExcel } from '~/Utils/Helpers';
import Header from '~/Components/Header/Header';
import {
  listarMoedas,
  gerarRelatorio,
} from '~/Services/Relatorios/RelatorioCotacaoMoedaForteService';
import { customStyles } from '~/Styles/reactSelectStyles';
import CardCotacao from './CardCotacao';

const schemaRelatorio = Yup.object().shape({
  codigoAtivo: Yup.number()
    .typeError('Selecione uma moeda')
    .required('Selecione uma moeda')
    .integer()
    .positive(),
  dataIni: Yup.date()
    .typeError('Data inicial inválida')
    .required('Data inicial é obrigatória'),
  dataFim: Yup.date()
    .typeError('Data final inválida')
    .required('Data final é obrigatória')
    .min(Yup.ref('dataIni'), 'Data final deve ser maior ou igual à data inicial'),
});

const fmt4 = (v) =>
  v == null
    ? ''
    : Number(v).toLocaleString('pt-BR', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });

const fmt5 = (v) =>
  v == null
    ? ''
    : Number(v).toLocaleString('pt-BR', {
        minimumFractionDigits: 5,
        maximumFractionDigits: 5,
      });

const fmtData = (iso) => new Date(iso).toLocaleDateString('pt-BR');

const fmtDateStr = (str) => {
  if (!str) return '';
  const parts = str.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : str;
};

const extrairMensagemErro = (err) =>
  err && err.message ? err.message : 'Erro inesperado ao gerar relatório';

const RelatorioCotacaoMoedaForte = () => {
  const [moedas, setMoedas] = useState([]);
  const [resposta, setResposta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMoedas, setLoadingMoedas] = useState(true);
  const [erro, setErro] = useState(null);
  const [moedaSelecionada, setMoedaSelecionada] = useState(null);
  const [ultimosPeriodo, setUltimosPeriodo] = useState({ dataIni: '', dataFim: '' });
  const [ordenacao, setOrdenacao] = useState({ coluna: 'data', direcao: 'asc' });
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 30;

  useEffect(() => {
    let ativo = true;
    setLoadingMoedas(true);
    listarMoedas()
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        const opcoes = lista.map((m) => ({
          value: m.codigo_ativo,
          label: m.nome,
          nome: m.nome,
        }));
        setMoedas(opcoes);
      })
      .catch(() => {
        if (ativo) setErro('Erro ao carregar lista de moedas.');
      })
      .finally(() => {
        if (ativo) setLoadingMoedas(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const handleSubmit = async (values) => {
    setLoading(true);
    setErro(null);
    setResposta(null);
    setOrdenacao({ coluna: 'data', direcao: 'asc' });
    setPaginaAtual(1);
    const moeda = moedas.find((m) => m.value === values.codigoAtivo);
    setMoedaSelecionada(moeda);
    setUltimosPeriodo({ dataIni: values.dataIni, dataFim: values.dataFim });
    try {
      const payload = {
        codigo_ativo: values.codigoAtivo,
        data_ini: values.dataIni,
        data_fim: values.dataFim,
      };
      const res = await gerarRelatorio(payload);
      setResposta(res);
    } catch (err) {
      setErro(extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  };

  const handleExportar = async () => {
    if (!resposta || !resposta.linhas || resposta.linhas.length === 0) return;
    const linhas = linhasOrdenadas.map((l) => ({
      data: fmtData(l.data),
      cotacao: fmt4(l.cotacao),
      variacao_acumulada: fmt4(l.variacao_acumulada),
      variacao_mensal: fmt4(l.variacao_mensal),
      variacao_anual: fmt4(l.variacao_anual),
      cotacao2: resposta.exibir_cotacao2 ? fmt5(l.cotacao2) : null,
      variacao_acumulada2: resposta.exibir_cotacao2
        ? fmt5(l.variacao_acumulada2)
        : null,
      variacao_mensal2: resposta.exibir_cotacao2
        ? fmt5(l.variacao_mensal2)
        : null,
      variacao_anual2: resposta.exibir_cotacao2
        ? fmt5(l.variacao_anual2)
        : null,
    }));
    try {
      await salvarEmExcel(linhas, '/exportador/excel/relatorio-cotacao-moeda-forte');
    } catch (err) {
      setErro(extrairMensagemErro(err));
    }
  };

  const linhasOrdenadas = useMemo(() => {
    if (!resposta || !resposta.linhas) return [];
    const { coluna, direcao } = ordenacao;
    const copia = [...resposta.linhas];
    copia.sort((a, b) => {
      const va = a[coluna];
      const vb = b[coluna];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (coluna === 'data') {
        const da = new Date(va).getTime();
        const db = new Date(vb).getTime();
        return direcao === 'asc' ? da - db : db - da;
      }
      return direcao === 'asc' ? va - vb : vb - va;
    });
    return copia;
  }, [resposta, ordenacao]);

  const temDados = linhasOrdenadas.length > 0;

  const totalPaginas = Math.ceil(linhasOrdenadas.length / itensPorPagina);

  const linhasPagina = useMemo(() => {
    const inicio = (paginaAtual - 1) * itensPorPagina;
    return linhasOrdenadas.slice(inicio, inicio + itensPorPagina);
  }, [linhasOrdenadas, paginaAtual]);

  const indiceInicio =
    linhasOrdenadas.length === 0 ? 0 : (paginaAtual - 1) * itensPorPagina + 1;
  const indiceFim = Math.min(
    paginaAtual * itensPorPagina,
    linhasOrdenadas.length
  );

  const irParaPagina = (numero) => {
    setPaginaAtual(numero);
  };

  const renderPaginas = () => {
    const itens = [];
    const maxVisiveis = 5;
    const inicio = Math.max(1, paginaAtual - Math.floor(maxVisiveis / 2));
    const fim = Math.min(totalPaginas, inicio + maxVisiveis - 1);

    if (inicio > 1) {
      itens.push(
        <Pagination.Item key={1} onClick={() => irParaPagina(1)}>
          1
        </Pagination.Item>
      );
      if (inicio > 2) {
        itens.push(<Pagination.Ellipsis key="inicio-ellipsis" />);
      }
    }

    for (let pagina = inicio; pagina <= fim; pagina += 1) {
      itens.push(
        <Pagination.Item
          key={pagina}
          active={pagina === paginaAtual}
          onClick={() => irParaPagina(pagina)}
        >
          {pagina}
        </Pagination.Item>
      );
    }

    if (fim < totalPaginas) {
      if (fim < totalPaginas - 1) {
        itens.push(<Pagination.Ellipsis key="fim-ellipsis" />);
      }
      itens.push(
        <Pagination.Item
          key={totalPaginas}
          onClick={() => irParaPagina(totalPaginas)}
        >
          {totalPaginas}
        </Pagination.Item>
      );
    }

    return itens;
  };

  const aoOrdenar = (coluna) => {
    setOrdenacao((atual) => ({
      coluna,
      direcao:
        atual.coluna === coluna && atual.direcao === 'asc' ? 'desc' : 'asc',
    }));
    setPaginaAtual(1);
  };

  const renderIconeOrdem = (coluna) => {
    if (ordenacao.coluna !== coluna) {
      return <FaSort className="ml-1" style={{ opacity: 0.5 }} />;
    }
    return ordenacao.direcao === 'asc' ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  const estiloHeaderOrdenavel = {
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };
  return (
    <div className="container-fluid mt-3">
      <Header title="Cotação Moeda Forte" />
      <Card className="my-3">
        <Card.Body>
          <Formik
            initialValues={{ codigoAtivo: '', dataIni: '', dataFim: '' }}
            validationSchema={schemaRelatorio}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, setFieldValue, isValid, dirty }) => (
              <Form>
                <div className="row">
                  <div className="col-12 col-md-4 mb-3">
                    <label className="small mb-1">Moeda *</label>
                    <Select
                      options={moedas}
                      styles={customStyles}
                      placeholder={loadingMoedas ? 'Carregando...' : 'Selecione a moeda'}
                      isLoading={loadingMoedas}
                      isDisabled={loadingMoedas}
                      onChange={(opt) =>
                        setFieldValue('codigoAtivo', opt ? opt.value : '')
                      }
                      noOptionsMessage={() => 'Nenhuma moeda encontrada'}
                    />
                    {touched.codigoAtivo && errors.codigoAtivo && (
                      <div className="text-danger small mt-1">
                        {errors.codigoAtivo}
                      </div>
                    )}
                  </div>
                  <div className="col-12 col-md-3 mb-3">
                    <label className="small mb-1">Data Inicial *</label>
                    <Field
                      name="dataIni"
                      type="date"
                      className={`form-control${
                        touched.dataIni && errors.dataIni ? ' is-invalid' : ''
                      }`}
                    />
                    {touched.dataIni && errors.dataIni && (
                      <div className="invalid-feedback">{errors.dataIni}</div>
                    )}
                  </div>
                  <div className="col-12 col-md-3 mb-3">
                    <label className="small mb-1">Data Final *</label>
                    <Field
                      name="dataFim"
                      type="date"
                      className={`form-control${
                        touched.dataFim && errors.dataFim ? ' is-invalid' : ''
                      }`}
                    />
                    {touched.dataFim && errors.dataFim && (
                      <div className="invalid-feedback">{errors.dataFim}</div>
                    )}
                  </div>
                  <div className="col-12 col-md-2 mb-3 d-flex align-items-end">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || !(isValid && dirty)}
                      className="w-100"
                    >
                      {loading ? 'Gerando...' : 'Gerar Relatório'}
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>

      {loading && <Loading label="Gerando relatório..." />}

      {erro && <Alert variant="danger">{erro}</Alert>}

      {resposta && !temDados && (
        <Alert variant="warning">
          Nenhuma cotação encontrada para{' '}
          <strong>{moedaSelecionada ? moedaSelecionada.nome : ''}</strong> no
          período de{' '}
          <strong>{fmtDateStr(ultimosPeriodo.dataIni)}</strong> a{' '}
          <strong>{fmtDateStr(ultimosPeriodo.dataFim)}</strong>.
        </Alert>
      )}

      {temDados && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Button variant="success" size="sm" onClick={handleExportar}>
              Exportar Excel
            </Button>
            {totalPaginas > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.First
                  onClick={() => irParaPagina(1)}
                  disabled={paginaAtual === 1}
                >
                  {'«'}
                </Pagination.First>
                <Pagination.Prev
                  onClick={() => irParaPagina(paginaAtual - 1)}
                  disabled={paginaAtual === 1}
                >
                  {'‹'}
                </Pagination.Prev>
                {renderPaginas()}
                <Pagination.Next
                  onClick={() => irParaPagina(paginaAtual + 1)}
                  disabled={paginaAtual === totalPaginas}
                >
                  {'›'}
                </Pagination.Next>
                <Pagination.Last
                  onClick={() => irParaPagina(totalPaginas)}
                  disabled={paginaAtual === totalPaginas}
                >
                  {'»'}
                </Pagination.Last>
              </Pagination>
            </div>
          )}
          </div>

          {/* Tabela desktop ≥768px */}
          <div className="d-none d-md-block">
            <Table striped bordered hover size="sm" responsive>
              <thead>
                <tr>
                  <th style={estiloHeaderOrdenavel} onClick={() => aoOrdenar('data')}>
                    Data{renderIconeOrdem('data')}
                  </th>
                  <th style={estiloHeaderOrdenavel} onClick={() => aoOrdenar('cotacao')}>
                    Cotação{renderIconeOrdem('cotacao')}
                  </th>
                  <th style={estiloHeaderOrdenavel} onClick={() => aoOrdenar('variacao_acumulada')}>
                    Var. Acum. (%){renderIconeOrdem('variacao_acumulada')}
                  </th>
                  <th style={estiloHeaderOrdenavel} onClick={() => aoOrdenar('variacao_mensal')}>
                    Var. Mensal (%){renderIconeOrdem('variacao_mensal')}
                  </th>
                  <th style={estiloHeaderOrdenavel} onClick={() => aoOrdenar('variacao_anual')}>
                    Var. Anual (%){renderIconeOrdem('variacao_anual')}
                  </th>
                  {resposta.exibir_cotacao2 && (
                    <>
                      <th style={estiloHeaderOrdenavel} onClick={() => aoOrdenar('cotacao2')}>
                        Cotação 2{renderIconeOrdem('cotacao2')}
                      </th>
                      <th style={estiloHeaderOrdenavel} onClick={() => aoOrdenar('variacao_acumulada2')}>
                        Var. Acum. 2 (%){renderIconeOrdem('variacao_acumulada2')}
                      </th>
                      <th style={estiloHeaderOrdenavel} onClick={() => aoOrdenar('variacao_mensal2')}>
                        Var. Mensal 2 (%){renderIconeOrdem('variacao_mensal2')}
                      </th>
                      <th style={estiloHeaderOrdenavel} onClick={() => aoOrdenar('variacao_anual2')}>
                        Var. Anual 2 (%){renderIconeOrdem('variacao_anual2')}
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {linhasPagina.map((l) => (
                  <tr key={l.data}>
                    <td>{fmtData(l.data)}</td>
                    <td>{fmt4(l.cotacao)}</td>
                    <td>{fmt4(l.variacao_acumulada)}</td>
                    <td>{fmt4(l.variacao_mensal)}</td>
                    <td>{fmt4(l.variacao_anual)}</td>
                    {resposta.exibir_cotacao2 && (
                      <>
                        <td>{fmt5(l.cotacao2)}</td>
                        <td>{fmt5(l.variacao_acumulada2)}</td>
                        <td>{fmt5(l.variacao_mensal2)}</td>
                        <td>{fmt5(l.variacao_anual2)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Cards mobile <768px */}
          <div className="d-block d-md-none">
            {linhasPagina.map((l) => (
              <CardCotacao
                key={l.data}
                linha={l}
                exibirCotacao2={resposta.exibir_cotacao2}
                fmt4={fmt4}
                fmt5={fmt5}
                fmtData={fmtData}
              />
            ))}
          </div>

          {totalPaginas > 1 && (
            <div className="d-flex justify-content-right mt-3">
              <Pagination>
                <Pagination.First
                  onClick={() => irParaPagina(1)}
                  disabled={paginaAtual === 1}
                >
                  {'«'}
                </Pagination.First>
                <Pagination.Prev
                  onClick={() => irParaPagina(paginaAtual - 1)}
                  disabled={paginaAtual === 1}
                >
                  {'‹'}
                </Pagination.Prev>
                {renderPaginas()}
                <Pagination.Next
                  onClick={() => irParaPagina(paginaAtual + 1)}
                  disabled={paginaAtual === totalPaginas}
                >
                  {'›'}
                </Pagination.Next>
                <Pagination.Last
                  onClick={() => irParaPagina(totalPaginas)}
                  disabled={paginaAtual === totalPaginas}
                >
                  {'»'}
                </Pagination.Last>
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RelatorioCotacaoMoedaForte;
